import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import styles from './UserForm.module.scss';
import { DEBUG } from '../../utils/constants';

import { LanguageContext } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import PasswordStrengthIndicator from '../../utils/PasswordStrengthIndicator/PasswordStrengthIndicator';
import CountrySelector from '../../utils/CountrySelector/CountrySelector';
import IosCompatibleInput from '../../utils/IosCompatibleInput/IosCompatibleInput';

// API
import { createUser, connectUser, isPseudoExists, isEmailExists } from '../../utils/api/usersAPI';
import { logSignIn, logFormChange, logSubscribe, logConnect, reportClientError } from '../../utils/api/reportAPI';
// import { reportClientError } from '../../utils/api/reportClientErrorAPI';

// Import des traductions 
import translations from '../../utils/translations/UserFormTranslations';
import countries from '../../utils/CountrySelector/allCountries';


// const debug = DEBUG;
const debug = true;


const UserForm = () => {

  const { login, clearUserDatas } = useAuth(); // Utilisation du contexte d'authentification
  const navigate = useNavigate();

  // Utilisation du contexte de langue global
  const { language } = useContext(LanguageContext);

  // Accès simplifié aux traductions
  const t = translations[language] || translations.fr;

// ------------------- STATES -------------------------

  const [loading, setLoading] = useState(false); // Ajout d’un état pour éviter plusieurs requêtes
  const [isLogin, setIsLogin] = useState(true); // Ajouter une valeur à tester en localStorage pour voir quel formulaire charger en priorité
  const [formData, setFormData] = useState({
    pseudo: '',
    email: '',
    password: '',
    confirmPassword: '',
    postalCode: '',
    country: '', 
    loginIdentifier: '', // pseudo ou email pour la connexion
    loginPassword: '',
    captcha: false,
    terms: false,
  });
  const [errors, setErrors] = useState({}); // Stockage des erreurs

  // Gestion visibilité du mot de passe
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);


// ------------------- EFFECTS -------------------------

  useEffect(() => {
  const timeout = setTimeout(() => {
    if (!formData.email) {
      reportClientError({
        type: 'form_watchdog',
        context: 'UserForm > email non rempli après délai',
        message: 'Champ email resté vide 40s après affichage',
      }, formData // ✅ bien à part);
      )}
  }, 40000); // 40 secondes

  return () => clearTimeout(timeout);
}, []);

useEffect(() => {
  if (!isLogin) {
    logSignIn();
  }
}, [isLogin]);

// ------------------- EVENTS -------------------------

  const handleChange = async (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === 'checkbox' ? checked : value;
    let errorMessage = "";
  
    // Appliquer une fonction spécifique si nécessaire
    const validationFunctions = {
      pseudo: validatePseudo,
      email: validateEmail,
      password: validatePassword,
      confirmPassword: (val) => validateConfirmPassword(val, formData.password),
      postalCode: validatePostalCode,
      country: validateCountry,
      captcha: validateCaptcha,
      terms: validateTerms
    };
  
    // Vérification si c'est une fonction async (API call)
    if (validationFunctions[name]) {
      errorMessage = name === "pseudo" || name === "email" ? await validationFunctions[name](newValue) : validationFunctions[name](newValue);
    }
  
    setFormData({
      ...formData,
      [name]: newValue,
    });

    setErrors({
      ...errors,
      [name]: errorMessage, // Mise à jour des erreurs
    });

    if (debug) logFormChange({ field: name, value: newValue, error: errorMessage });

  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true); // Bloque le formulaire en attendant la réponse

    if (isLogin) {
      try {
        const loginId = cleanInput(formData.loginIdentifier);
        const loginPwd = formData.loginPassword;
        const userData = await connectUser(loginId, loginPwd);
        if (userData.authToken) {
          login(userData); // Stocke les infos utilisateur dans le contexte
        } else {
          setErrors((prev) => ({ ...prev, general: t.invalidCredentials }));
          if (debug) reportClientError( { type: 'login', context: 'UserForm > handleSubmit', message: t.invalidCredentials }, formData );
          setLoading(false);
        }
      } catch (error) {
        setErrors((prev) => ({ ...prev, general: t.serverError }));
        if (debug) reportClientError( { type: 'login', context: 'UserForm > handleSubmit', message: t.serverError }, formData );
        setLoading(false);
      }
    } else {
      // Petit délai pour laisser le DOM finir les blur/input
      await new Promise((resolve) => setTimeout(resolve, 50));
      
      // Traitement du formulaire d'inscription
      // Vérifier tous les champs avant soumission
      const newErrors = {
        pseudo: await validatePseudo(formData.pseudo),
        email: await validateEmail(formData.email),
        password: validatePassword(formData.password),
        country: validateCountry(formData.country),
        confirmPassword: validateConfirmPassword(formData.confirmPassword, formData.password),
        captcha: validateCaptcha(formData.captcha),
        terms: validateTerms(formData.terms),
      };

      setErrors(newErrors);

      // Vérifier s'il y a des erreurs
      const hasErrors = Object.values(newErrors).some(error => error !== "");

      if (!hasErrors) {
        try {
          const userData = await createUser(formData.email, formData.pseudo, formData.password, formData.confirmPassword, formData.postalCode, formData.country);
          if (userData.authToken) {
            clearUserDatas();
            login(userData); // Stocke les infos utilisateur dans le contexte
            // console.log("Formulaire soumis avec succès 🎉", formData);
          } else {
            setErrors((prev) => ({ ...prev, general: t.registrationError }));
            if (debug) reportClientError( { type: 'subscribe', context: 'UserForm > handleSubmit', message: t.registrationError, stack: errors }, formData );
          }
        } catch (error) {
          setErrors((prev) => ({ ...prev, general: t.serverError }));
          if (debug) reportClientError( { type: 'subscribe', context: 'UserForm > handleSubmit', message: t.serverError, stack: errors }, formData );
        } finally {
          setLoading(false);
        }
      } else {
        if (debug) console.log(t.fixErrorsBeforeSubmit);
        if (debug) console.log(errors);
        setLoading(false);
      }
      
    }
  };

  // const handleSubmit = async (e) => {
  //   e.preventDefault();
    
  //   if (loading) return; // Éviter les soumissions multiples
  //   setLoading(true); // Bloque le formulaire en attendant la réponse
    
  //   // Réinitialiser les erreurs générales précédentes
  //   setErrors(prev => ({ ...prev, general: "" }));

  //   try {
  //     if (isLogin) {
  //       // Connexion - code inchangé
  //       try {
  //         const userData = await connectUser(formData.loginIdentifier, formData.loginPassword);
  //         if (userData && userData.authToken) {
  //           login(userData); // Stocke les infos utilisateur dans le contexte
  //         } else {
  //           setErrors((prev) => ({ ...prev, general: t.invalidCredentials }));
  //         }
  //       } catch (error) {
  //         console.error("Erreur lors de la connexion:", error);
  //         setErrors((prev) => ({ ...prev, general: t.serverError }));
  //       }
  //     } else {
  //       // INSCRIPTION
  //       console.log("Début du processus d'inscription");
        
  //       // 1. Effectuer les validations synchrones
  //       const localErrors = {};
        
  //       // Vérifier le pseudo
  //       if (!formData.pseudo || formData.pseudo.trim() === '') {
  //         localErrors.pseudo = t.usernameRequired;
  //       }
        
  //       // Vérifier l'email
  //       if (!formData.email || formData.email.trim() === '') {
  //         localErrors.email = t.emailRequired;
  //       } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
  //         localErrors.email = t.invalidEmailFormat;
  //       }
        
  //       // Vérifier le code postal
  //       if (!formData.postalCode || formData.postalCode.trim() === '') {
  //         localErrors.postalCode = t.postalCodeRequired;
  //       }
        
  //       // Vérifier le mot de passe
  //       const passwordError = validatePassword(formData.password);
  //       if (passwordError) {
  //         localErrors.password = passwordError;
  //       }
        
  //       // Vérifier la confirmation du mot de passe
  //       if (formData.password !== formData.confirmPassword) {
  //         localErrors.confirmPassword = t.passwordMismatch;
  //       }
        
  //       // Vérifier le captcha
  //       if (!formData.captcha) {
  //         localErrors.captcha = t.captchaRequired;
  //       }
        
  //       // Vérifier les conditions
  //       if (!formData.terms) {
  //         localErrors.terms = t.acceptTermsRequired;
  //       }
        
  //       // Mettre à jour les erreurs
  //       setErrors(localErrors);
        
  //       // S'il y a des erreurs, arrêter la soumission
  //       if (Object.keys(localErrors).length > 0) {
  //         console.log("Erreurs de validation détectées:", localErrors);
  //         setLoading(false);
  //         return;
  //       }
        
  //       console.log("Validation de base réussie, envoi de la requête...");
        
  //       // 2. Si toutes les validations sont passées, envoyer la requête
  //       try {
  //         const userData = await createUser(
  //           formData.email,
  //           formData.pseudo,
  //           formData.password,
  //           formData.confirmPassword,
  //           formData.postalCode
  //         );
          
  //         console.log("Réponse du serveur:", userData);
          
  //         if (userData && userData.authToken) {
  //           clearUserDatas();
  //           login(userData);
  //           console.log("Inscription réussie ✅");
  //         } else {
  //           console.error("Réponse invalide du serveur:", userData);
  //           setErrors({ general: t.registrationError });
  //         }
  //       } catch (error) {
  //         console.error("Erreur lors de l'inscription:", error);
  //         setErrors({ 
  //           general: error.message || t.serverError 
  //         });
  //       }
  //     }
  //   } catch (error) {
  //     console.error("Erreur générale:", error);
  //     setErrors({ general: t.serverError });
  //   } finally {
  //     setLoading(false);
  //   }
  // };


// ------------------- FUNCTIONS -------------------------

const cleanInput = (str) =>
  (str || '')
    .replace(/[\u200B-\u200D\uFEFF\u2060]/g, '') // caractères invisibles
    .normalize("NFD")                            // décomposition accents
    .replace(/[\u0300-\u036f]/g, '')             // suppression accents
    .trim();

const validatePseudo = async (value) => {
  let trimmedValue = cleanInput(value); // Supprime les espaces en début et fin
  let errorMessage = "";

  // Vérification du format (autoriser lettres, chiffres, tirets, underscore et caractères européens)
  const pseudoRegex = /^[A-Za-z0-9À-ÖØ-öø-ÿ_-]+$/;
  if (!pseudoRegex.test(trimmedValue)) {
    errorMessage = t.invalidUsernameFormat;
    if (debug) reportClientError( { type: 'subscribe', context: 'UserForm > validatePseudo', message: errorMessage, stack: errors, } );
  } else {
    // Vérification avec l'API si le pseudo est déjà pris
    const exists = await isPseudoExists(trimmedValue);
    if (exists.exists === true) {
      errorMessage = t.usernameTaken;
      if (debug) reportClientError( { type: 'subscribe', context: 'UserForm > validatePseudo', message: errorMessage, stack: errors, } );
    }
  }

  return errorMessage; // Retourne "" si tout est bon, sinon un message d'erreur
};

const validateEmail = async (value) => {
  let trimmedValue = cleanInput(value); // Supprime les espaces en début et fin
  let errorMessage = "";

  // Vérification du format email
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(trimmedValue)) {
    errorMessage = t.invalidEmailFormat;
    if (debug) reportClientError( { type: 'subscribe', context: 'UserForm > validateEmail', message: errorMessage, stack: { originalInput: value, cleanedInput: trimmedValue, error: errorMessage }} );
  } else {
    // Vérification avec l'API si l'email est déjà utilisé
    const exists = await isEmailExists(trimmedValue);
    if (exists && exists.exists === true) {
      errorMessage = t.emailTaken;
      if (debug) reportClientError( { type: 'subscribe', context: 'UserForm > validateEmail', message: errorMessage, stack: { originalInput: value, cleanedInput: trimmedValue, error: errorMessage }} );
    }
  }

  return errorMessage; // Retourne "" si tout est bon, sinon un message d'erreur
};

const validatePassword = (value) => {
  let errorMessage = "";
  
  // Vérifications spécifiques
  if (value.length < 12) {
    errorMessage = t.passwordTooShort;
  } else if (!/[A-Z]/.test(value)) {
    errorMessage = t.passwordMissingUppercase;
  } else if (!/[a-z]/.test(value)) {
    errorMessage = t.passwordMissingLowercase;
  } else if (!/[0-9]/.test(value)) {
    errorMessage = t.passwordMissingNumber;
  } else if (!/[\W_]/.test(value)) {
    errorMessage = t.passwordMissingSpecial;
  }

  return errorMessage; // Retourne "" si tout est bon, sinon un message d'erreur
};

const validateConfirmPassword = (value, password) => {
  if (!value) {
    return t.confirmPasswordRequired;
  }
  if (value !== password) {
    return t.passwordMismatch;
  }
  return ""; // Pas d'erreur
};

const validatePostalCode = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return t.postalCodeRequired;

  // Autorise des formats comme : 75001, SW1A 1AA, H2Z 1Y7, 10115, etc.
  // const postalCodeRegex = /^[A-Za-z0-9\s\-]{3,10}$/;

  // if (!postalCodeRegex.test(trimmed)) {
  //   return t.invalidPostalCode;
  // }
  const postalCodeRegexes = [
    /^\d{5}$/, // France, Allemagne, US simple
    /^[A-Za-z]\d[A-Za-z][\s-]?\d[A-Za-z]\d$/, // Canada
    /^[A-Za-z]{1,2}\d[A-Za-z0-9]?\s?\d[A-Za-z]{2}$/, // UK
    /^\d{4}$/ // Belgique, Suisse
  ];

  const isValidPostalCode = postalCodeRegexes.some(regex => regex.test(trimmed));

  if (!isValidPostalCode) {
    return t.invalidPostalCode;
  }

  return "";
};

const validateCountry = (code) => {
  const found = countries.find((c) => c.code === code);
  return found ? "" : t.countryRequired;
};

const validateCaptcha = (value) => value ? "" : t.captchaRequired;

const validateTerms = (value) => value ? "" : t.acceptTermsRequired;

const togglePasswordVisibility = (passwordField) => {
  if (passwordField === 'password') {
    setShowPassword(!showPassword);
  } else if (passwordField === 'confirmPassword') {
    setShowConfirmPassword(!showConfirmPassword);
  } else if (passwordField === 'loginPassword') {
    setShowLoginPassword(!showLoginPassword);
  }
};

const handleFocus = (e) => {
  // Force le navigateur à reconsidérer les attributs lors du focus
  // e.target.setAttribute('autocomplete', 'new-password');
  // e.target.setAttribute('autocorrect', 'off');

  if (debug) logFormChange({
    field: e.target.name,
    action: 'focus',
    origin: e.nativeEvent.inputType || 'unknown',
  });
};

// ------------------- DISPLAY -------------------------

  return (
    <div className={styles.authContainer}>
      <h2 className={styles.title}>{t.accessApp}</h2>
      <p className={styles.subTitle}>{t.accountRequired}</p>
      <form onSubmit={handleSubmit} className={styles.authForm}>           
          
        {isLogin ? (
          <>
            <div className={styles.formGroup}>
              <label htmlFor="loginIdentifier">{t.usernameOrEmail}</label>
              {/* <input type="text" id="loginIdentifier" name="loginIdentifier" value={formData.loginIdentifier} onChange={handleChange} required /> */}
              <IosCompatibleInput
                type="text"
                id="loginIdentifier"
                name="loginIdentifier"
                value={formData.loginIdentifier}
                onFocus={handleFocus}
                onChange={handleChange}
                debounce={250} // optionnel, par défaut 300ms
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="loginPassword">{t.password}</label>
              <div className={styles.passwordInputContainer}>
                <input type={showLoginPassword ? "text" : "password"} id="loginPassword" name="loginPassword" value={formData.loginPassword} onChange={handleChange} required />
                <button type="button" onClick={() => togglePasswordVisibility('loginPassword')} className={styles.togglePasswordButton} aria-label={showLoginPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}>
                  {showLoginPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              
            </div>
            {errors.general && <p className={styles.error}>{errors.general}</p>}
            <button className={styles.button} type="submit" disabled={loading}>
              {loading ? t.connecting : t.login}
            </button>
            <p className={styles.toggleText}>
            <button onClick={() => navigate("/reset-request")} className={styles.toggleButton}>
              {t.forgotPassword}
            </button>
          </p>
          </>
        ) : (
          <>
          <div className={styles.formGroup}>
              <label htmlFor="pseudo">{t.username}</label>
              <IosCompatibleInput
                type="text" 
                id="pseudo" 
                name="pseudo" 
                value={formData.pseudo}
                onChange={handleChange}
                debounce={250} // optionnel, par défaut 300ms
              />
                
              {errors.pseudo && <p className={styles.error}>{errors.pseudo}</p>}
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="email">{t.email}</label>
              <IosCompatibleInput 
                type="email" 
                id="email" 
                name="email" 
                value={formData.email} 
                onChange={handleChange} 
                required />
              {errors.email && <p className={styles.error}>{errors.email}</p>}
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="postalCode">{t.postalCode}</label>
              {/* <input type="text" autoComplete="new-password" id="postalCode" name="postalCode" value={formData.postalCode} onChange={handleChange} required /> */}
              <IosCompatibleInput
                type="text"
                id="postalCode" 
                name="postalCode"
                value={formData.postalCode}
                onChange={handleChange}
                debounce={250} // optionnel, par défaut 300ms
              />
              {errors.postalCode && <p className={styles.error}>{errors.postalCode}</p>}
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="country">{t.country}</label>
              {/* <select
                id="country"
                name="country"
                value={formData.country}
                onChange={handleChange}
                required
              >
                <option value="">{t.selectCountry}</option>
                <option value="FR">🇫🇷 France</option>
                <option value="BE">🇧🇪 Belgique</option>
                <option value="CH">🇨🇭 Suisse</option>
                <option value="CA">🇨🇦 Canada</option>
                <option value="IT">🇮🇹 Italie</option>
                <option value="ES">🇪🇸 Espagne</option>
                <option value="DE">🇩🇪 Allemagne</option>
              </select> */}
              <CountrySelector value={formData.country} onChange={(selectedCode) => handleChange({ target: { name: 'country', value: selectedCode } }) } />
              {errors.country && <p className={styles.error}>{errors.country}</p>}
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="password">{t.password}</label>
              {/* <input type="password" id="password" name="password" value={formData.password} onChange={handleChange} required /> */}
              <div className={styles.passwordInputContainer}>
                <input type={showPassword ? "text" : "password"} autoComplete="new-password" id="password" name="password" value={formData.password} onChange={handleChange} required />
                <button type="button" onClick={() => togglePasswordVisibility('password')} className={styles.togglePasswordButton} aria-label={showLoginPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}>
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              
              {errors.password && <p className={styles.error}>{errors.password}</p>}
              <PasswordStrengthIndicator password={formData.password} />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="confirmPassword">{t.confirmPassword}</label>
              <input type="password" id="confirmPassword" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} required />
              {errors.confirmPassword && <p className={styles.error}>{errors.confirmPassword}</p>}
            </div>
            <div className={styles.checkboxGroup}>
              <input type="checkbox" id="captcha" name="captcha" checked={formData.captcha} onChange={handleChange} />
              <label htmlFor="captcha">{t.captchaLabel}</label>
            </div>
            {errors.captcha && <p className={styles.error}>{errors.captcha}</p>}
            <div className={styles.checkboxGroup}>
              <input type="checkbox" id="terms" name="terms" checked={formData.terms} onChange={handleChange} />
              <label htmlFor="terms">{t.iHaveRead}<a className={styles.anchor} onClick={() => navigate("/conditions")}>{t.termsAndConditions}</a>{t.and}<a className={styles.anchor} onClick={() => navigate("/privacy")}>{t.privacy}</a></label>
            </div>
            {errors.terms && <p className={styles.error}>{errors.terms}</p>}
            <button className={styles.button} type="submit" disabled={loading}>{t.register}</button>
          </>
        )}
      </form>
      <div className={styles.toggleContainer}>
        {isLogin ? (
          <p className={styles.toggleText}>
            {t.noAccount}{' '}
            <button onClick={() => setIsLogin(false)} className={styles.toggleButton}>
              {t.createAccount}
            </button>
          </p>
        ) : (
          <p className={styles.toggleText}>
            {t.alreadyHaveAccount}{' '}
            <button onClick={() => setIsLogin(true)} className={styles.toggleButton}>
              {t.goToLogin}
            </button>
          </p>
        )}
      </div>
    </div>
  );
};

export default UserForm;
